// Draft recovery and duplicate warning, shared by both incident form pages.
//
// The portal form and the webview form are required to behave identically, so
// the logic and the UI live here once rather than being copied into each page.
// A page wires this with a handful of lines and applies the restore, which is
// the only part that has to touch its own form state.
import { useEffect, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import moment from "moment";

import {
  IncidentDraft,
  draftHasContent,
  dropDraft,
  loadDraft,
  saveDraft,
} from "./draft";
import { DuplicateHit } from "./duplicates";

const SAVE_DEBOUNCE_MS = 800;

export function useIncidentDraft(opts: {
  userId: string;
  incidentId: string | null;
  /** False in view mode, and until the record has loaded on an edit — saving
   *  before then would persist an empty form over a real draft. */
  enabled: boolean;
  fields: any;
  locality: any;
  locDefinition: any;
}) {
  const { userId, incidentId, enabled, fields, locality, locDefinition } = opts;
  const [draft, setDraft] = useState<IncidentDraft | null>(null);
  const timer = useRef<any>(null);

  // Look once, on mount. Re-reading later would resurrect a draft the admin
  // has already dismissed.
  useEffect(() => {
    setDraft(loadDraft(userId, incidentId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!enabled) return;
    if (!draftHasContent(fields)) return;

    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      saveDraft(userId, incidentId, { fields, locality, locDefinition });
    }, SAVE_DEBOUNCE_MS);

    return () => clearTimeout(timer.current);
  }, [enabled, fields, locality, locDefinition, userId, incidentId]);

  // Also flush on the way out: a debounce that never fires because the tab
  // closed first is exactly the case this feature exists for.
  useEffect(() => {
    const flush = () => {
      if (enabled && draftHasContent(fields)) {
        saveDraft(userId, incidentId, { fields, locality, locDefinition });
      }
    };
    window.addEventListener("pagehide", flush);
    return () => window.removeEventListener("pagehide", flush);
  }, [enabled, fields, locality, locDefinition, userId, incidentId]);

  return {
    draft,
    dismiss: () => {
      setDraft(null);
      dropDraft(userId, incidentId);
    },
    /** Call after a successful submit — the draft has become the record. */
    clear: () => {
      setDraft(null);
      dropDraft(userId, incidentId);
    },
  };
}

export function DraftBanner(props: {
  draft: IncidentDraft | null;
  isEdit: boolean;
  onRestore: () => void;
  onDiscard: () => void;
}) {
  const { draft, isEdit, onRestore, onDiscard } = props;
  if (!draft) return null;

  return (
    <div className="alert alert-warning d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
      <span>
        <i className="ri-history-line me-1" />
        Unsaved work from <strong>{moment(draft.at).fromNow()}</strong>
        {isEdit
          ? " — restoring replaces what is currently on this form."
          : " — you left this incident part-way through."}
      </span>
      <span className="d-flex gap-2">
        <Button size="sm" variant="primary" onClick={onRestore}>
          Restore it
        </Button>
        <Button size="sm" variant="light" onClick={onDiscard}>
          Discard
        </Button>
      </span>
    </div>
  );
}

export function DuplicateWarning(props: {
  hits: DuplicateHit[] | null;
  onProceed: () => void;
  onCancel: () => void;
}) {
  const { hits, onProceed, onCancel } = props;

  return (
    <Modal show={Boolean(hits && hits.length)} onHide={onCancel}>
      <Modal.Header closeButton>
        <h4 className="modal-title">Possible duplicate</h4>
      </Modal.Header>
      <Modal.Body>
        <p className="fs-16 mb-2">
          {hits && hits.length > 1
            ? `${hits.length} incidents were posted in this locality in the last 30 minutes that look like this one:`
            : "An incident was posted in this locality in the last 30 minutes that looks like this one:"}
        </p>
        <ul className="mb-2 ps-3">
          {(hits || []).map((h) => (
            <li key={h._id} className="mb-1">
              <strong>{h.title}</strong>
              {h.address ? ` — ${h.address}` : ""}
              {h.box ? ` (Box ${h.box})` : ""}
              <br />
              <span className="text-muted">
                created {h.minutesAgo === 0 ? "just now" : `${h.minutesAgo} min ago`} ·
                matched on {h.reason === "box" ? "box number" : "address"}
              </span>
            </li>
          ))}
        </ul>
        <p className="text-muted mb-0">
          A second alarm at the same address is a real second incident — this is
          only a heads-up, not a block.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onCancel}>
          <i className="ri-arrow-go-back-line me-1" />
          Let me check
        </Button>
        <Button variant="primary" onClick={onProceed}>
          <i className="ri-check-line me-1" />
          Submit anyway
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
